<template>
  <v-card class="mcp-carousel-card h-100" variant="outlined">
    <div class="mcp-carousel-card__head">
      <div
        :class="[
          'mcp-carousel-card__tile',
          { 'mcp-carousel-card__tile--fallback': !showStandaloneIcon }
        ]"
        :aria-label="`${item.title} server tile`"
      >
        <img
          v-if="showStandaloneIcon"
          :src="iconSrc"
          :alt="`${item.title} icon`"
          class="mcp-carousel-card__tile-image"
          @error="onIconError"
        >
        <span v-else>{{ tileLabel }}</span>
      </div>
      <div class="mcp-carousel-card__chips">
        <v-chip
          v-for="chip in stateChips"
          :key="chip.key"
          :color="chip.color"
          size="x-small"
          variant="tonal"
        >
          {{ chip.label }}
        </v-chip>
      </div>
    </div>

    <div class="mcp-carousel-card__body">
      <div class="text-h6 mcp-carousel-card__title">{{ item.title }}</div>
      <p class="text-body-2 text-medium-emphasis mcp-carousel-card__description">{{ item.description }}</p>

      <div v-if="hostnameLabel || repositoryHostLabel || item.licenseName" class="mcp-carousel-card__meta">
        <div v-if="hostnameLabel" class="mcp-carousel-card__meta-row">
          <v-icon size="x-small">mdi-link-variant</v-icon>
          <span>{{ hostnameLabel }}</span>
        </div>
        <div v-if="repositoryHostLabel" class="mcp-carousel-card__meta-row">
          <v-icon size="x-small">mdi-source-repository</v-icon>
          <span>{{ repositoryHostLabel }}</span>
        </div>
        <div v-if="item.licenseName" class="mcp-carousel-card__meta-row">
          <v-icon size="x-small">mdi-scale-balance</v-icon>
          <span>{{ item.licenseName }}</span>
        </div>
      </div>
    </div>

    <div class="mcp-carousel-card__foot">
      <div class="mcp-carousel-card__tools">
        <v-icon size="small" color="primary">mdi-tools</v-icon>
        <span>{{ toolCountLabel }}</span>
      </div>
      <v-btn color="primary" @click="emitPrimaryAction">
        {{ item.primaryActionLabel }}
      </v-btn>
    </div>
  </v-card>
</template>

<script lang="ts">
import { McpServerCarouselItem } from '@/utils/mcpCarousel'
import { getMcpServerIconPath } from '@/utils/mcpServerIcons'
import { Options, Vue } from 'vue-class-component'

interface CarouselStateChip {
  key: string
  label: string
  color: string
}

@Options({
  props: {
    item: Object
  },
  emits: ['primary-action']
})
export default class McpServerCarouselCard extends Vue {
  item!: McpServerCarouselItem
  iconLoadFailed = false

  get iconSrc(): string | null {
    return getMcpServerIconPath({
      serverName: this.item.serverName,
      title: this.item.title,
      url: this.item.url,
      repositoryUrl: this.item.repositoryUrl
    })
  }

  get showStandaloneIcon(): boolean {
    return !!this.iconSrc && this.iconLoadFailed !== true
  }

  get stateChips(): CarouselStateChip[] {
    const chips: CarouselStateChip[] = [
      {
        key: 'source',
        label: this.item.source === 'external' ? 'External' : 'Platform',
        color: this.item.source === 'external' ? 'primary' : 'secondary'
      }
    ]

    if (this.item.installed) {
      chips.push({
        key: 'installed',
        label: 'Installed',
        color: 'success'
      })
    } else if (this.item.primaryActionKind === 'install') {
      chips.push({
        key: 'available',
        label: 'Available',
        color: 'warning'
      })
    }

    if (this.item.authMode === 'oauth2') {
      chips.push({
        key: 'oauth',
        label: this.oauthChipLabel,
        color: this.item.authState === 'connected' ? 'success' : 'warning'
      })
    }

    chips.push({
      key: 'enabled',
      label: this.item.enabled ? 'Enabled' : 'Disabled',
      color: this.item.enabled ? 'success' : 'warning'
    })

    return chips
  }

  get oauthChipLabel(): string {
    if (this.item.authState === 'connected') {
      return 'OAuth Connected'
    }

    if (this.item.authState === 'reauth_required') {
      return 'Reconnect OAuth'
    }

    return 'OAuth Required'
  }

  get tileLabel(): string {
    const words = this.item.title
      .split(/[\s\-_/.]+/g)
      .map((word) => word.trim())
      .filter(Boolean)

    if (words.length >= 2) {
      return `${words[0][0] || ''}${words[1][0] || ''}`.toUpperCase()
    }

    if (words.length === 1 && words[0].length >= 2) {
      return words[0].slice(0, 2).toUpperCase()
    }

    const hostname = (this.hostnameLabel || '').replace(/[^a-zA-Z0-9]+/g, '')
    return hostname.slice(0, 2).toUpperCase() || 'MC'
  }

  get hostnameLabel(): string {
    return this.extractHostname(this.item.url)
  }

  get repositoryHostLabel(): string {
    return this.extractHostname(this.item.repositoryUrl)
  }

  get toolCountLabel(): string {
    if (this.item.toolCount === null) {
      return 'Tool count unavailable'
    }

    return `${this.item.toolCount} tool${this.item.toolCount === 1 ? '' : 's'}`
  }

  emitPrimaryAction(): void {
    this.$emit('primary-action', this.item.serverName)
  }

  onIconError(): void {
    this.iconLoadFailed = true
  }

  private extractHostname(value: string | undefined): string {
    if (!value) {
      return ''
    }

    try {
      return new URL(value).hostname.replace(/^www\./, '')
    } catch {
      return value
    }
  }
}
</script>

<style scoped>
.mcp-carousel-card {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  padding: 18px;
  border-radius: 16px;
  background: rgba(var(--v-theme-surface), 0.98);
}

.mcp-carousel-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.mcp-carousel-card__tile {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: rgb(var(--v-theme-primary));
  font-size: 0.95rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  flex: 0 0 52px;
  overflow: hidden;
}

.mcp-carousel-card__tile--fallback {
  background:
    radial-gradient(circle at top left, rgba(var(--v-theme-primary), 0.25), transparent 65%),
    rgba(var(--v-theme-on-surface), 0.05);
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
}

.mcp-carousel-card__tile-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.mcp-carousel-card__chips {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 6px;
}

.mcp-carousel-card__body {
  flex: 1;
  min-height: 0;
}

.mcp-carousel-card__title {
  margin-bottom: 8px;
  line-height: 1.2;
}

.mcp-carousel-card__description {
  margin: 0 0 16px;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.mcp-carousel-card__meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mcp-carousel-card__meta-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: rgba(var(--v-theme-on-surface), 0.68);
  overflow: hidden;
}

.mcp-carousel-card__meta-row span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mcp-carousel-card__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.1);
}

.mcp-carousel-card__tools {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.85rem;
  color: rgba(var(--v-theme-on-surface), 0.72);
}

@media (max-width: 640px) {
  .mcp-carousel-card__foot {
    flex-direction: column;
    align-items: stretch;
  }

  .mcp-carousel-card__foot :deep(.v-btn) {
    width: 100%;
  }
}
</style>
